"use client";

import React from "react";
import { useBooking } from "@/context/BookingContext";
import { useRouter } from "next/navigation";
import { CalendarIcon, Users, BedDouble } from "lucide-react";
import { Button } from "./ui/Button";
import { DatePickerWithRange } from "./ui/DatePickerWithRange";

export function SearchForm({ className, compact = false, mode = 'default' }: { className?: string, compact?: boolean, mode?: 'default' | 'widget' }) {
  const {
    checkIn,
    setCheckIn,
    checkOut,
    setCheckOut,
    adults,
    setAdults,
    children,
    setChildren,
    roomCount,
    setRoomCount,
    db,
    hotelId
  } = useBooking();

  const router = useRouter();

  const [isSearching, setIsSearching] = React.useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSearching(true);
    
    const params = new URLSearchParams();
    params.set("db", db);
    params.set("hotel_id", hotelId.toString());
    params.set("checkIn", checkIn.toISOString());
    params.set("checkOut", checkOut.toISOString());
    params.set("adults", adults.toString());
    params.set("children", children.toString());
    params.set("roomCount", roomCount.toString());

    if (mode === 'widget') {
      // Send message to parent window (the embedding page)
      window.parent.postMessage({
        type: 'search',
        params: params.toString()
      }, '*');
      setTimeout(() => setIsSearching(false), 500);
    } else {
      router.push(`/search?${params.toString()}`);
      // Reset loading state after a short delay to allow navigation to start
      setTimeout(() => setIsSearching(false), 1000);
    }
  };

  const handleDateRangeChange = (range: { from: Date; to: Date }) => {
    setCheckIn(range.from);
    setCheckOut(range.to);
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={`${className} ${compact ? 'flex flex-col xl:flex-row items-stretch xl:items-center gap-3 bg-white p-3 rounded-[2rem] border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)]' : 'flex flex-col gap-6 bg-white p-6 sm:p-8 rounded-[2rem] shadow-2xl border border-slate-100/50 backdrop-blur-sm'}`}
    >
      <div className={`grid ${compact ? 'grid-cols-1 md:grid-cols-12 gap-3 items-center w-full' : 'grid-cols-1 sm:grid-cols-2 gap-6'}`}>

        {/* Date Range Picker */}
        <div className={`${compact ? 'col-span-1 md:col-span-5 xl:col-span-4' : 'col-span-1 sm:col-span-2 space-y-3'}`}>
          {!compact && (
            <label className="text-sm font-medium text-slate-900 flex items-center gap-2 uppercase tracking-wider">
              <CalendarIcon className="w-4 h-4 text-slate-400" />
              Check-in & Check-out
            </label>
          )}
          <div className="relative group">
             {/* Icon removed for compact mode to avoid duplication as DatePickerWithRange might have one or we rely on the input styling */}
             <div className={compact ? "transition-all" : ""}>
                <DatePickerWithRange
                  from={checkIn}
                  to={checkOut}
                  onChange={handleDateRangeChange}
                  className={compact ? "w-full border-0 bg-slate-50 hover:bg-slate-100 shadow-none p-0 h-12 rounded-2xl transition-colors text-slate-900 font-medium" : "w-full"}
                />
             </div>
          </div>
        </div>

        {/* Separator for compact mode - hidden on smaller screens */}
        {compact && <div className="hidden xl:block w-px h-8 bg-slate-200 mx-1" />}

        {/* Guests */}
        <div className={`${compact ? 'col-span-1 md:col-span-4 xl:col-span-4' : 'space-y-3'}`}>
          {!compact && (
            <label className="text-sm font-medium text-slate-900 flex items-center gap-2 uppercase tracking-wider">
              <Users className="w-4 h-4 text-slate-400" />
              Guests
            </label>
          )}
          <div className={`flex gap-3 ${compact ? 'items-center' : ''}`}>
            <div className="relative flex-1 sm:w-full group">
                {/* Icon removed for compact mode */}
                <select
                  className={`w-full appearance-none outline-none text-slate-900 cursor-pointer ${
                    compact
                      ? 'pl-4 pr-8 h-12 font-medium bg-slate-50 hover:bg-slate-100 rounded-2xl transition-colors text-sm'
                      : 'p-3.5 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900 bg-white hover:border-slate-300 transition-all text-sm font-medium'
                  }`}
                  value={adults}
                  onChange={(e) => setAdults(Number(e.target.value))}
                >
                  {[1, 2, 3, 4, 5, 6].map((n) => (
                    <option key={n} value={n}>{n} Adult{n > 1 ? 's' : ''}</option>
                  ))}
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                </div>
            </div>

            <div className="relative flex-1 sm:w-full group">
                <select
                  className={`w-full appearance-none outline-none text-slate-900 cursor-pointer ${
                    compact
                      ? 'px-4 h-12 font-medium bg-slate-50 hover:bg-slate-100 rounded-2xl transition-colors text-sm'
                      : 'p-3.5 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900 bg-white hover:border-slate-300 transition-all text-sm font-medium'
                  }`}
                  value={children}
                  onChange={(e) => setChildren(Number(e.target.value))}
                >
                  {[0, 1, 2, 3, 4].map((n) => (
                    <option key={n} value={n}>{n} Child{n !== 1 ? 'ren' : ''}</option>
                  ))}
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                </div>
            </div>
          </div>
        </div>

        {/* Separator for compact mode */}
        {compact && <div className="hidden xl:block w-px h-8 bg-slate-200 mx-1" />}

        {/* Rooms */}
        <div className={`${compact ? 'col-span-1 md:col-span-3 xl:col-span-2' : 'space-y-3'}`}>
          {!compact && (
            <label className="text-sm font-medium text-slate-900 flex items-center gap-2 uppercase tracking-wider">
              <BedDouble className="w-4 h-4 text-slate-400" />
              Rooms
            </label>
          )}
          <div className="relative group">
             {/* Icon removed for compact mode */}
             <select
                className={`w-full appearance-none outline-none text-slate-900 cursor-pointer ${
                  compact
                    ? 'pl-4 pr-8 h-12 font-medium bg-slate-50 hover:bg-slate-100 rounded-2xl transition-colors text-sm'
                    : 'p-3.5 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900 bg-white hover:border-slate-300 transition-all text-sm font-medium'
                }`}
                value={roomCount}
                onChange={(e) => setRoomCount(Number(e.target.value))}
             >
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                  <option key={n} value={n}>{n} Room{n > 1 ? 's' : ''}</option>
                ))}
              </select>
              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
              </div>
          </div>
        </div>

      </div>

      <Button
        type="submit"
        disabled={isSearching}
        className={`${compact ? 'w-full xl:w-auto px-8 h-12 rounded-2xl shadow-lg hover:shadow-xl hover:shadow-slate-900/20 transition-all text-sm font-medium tracking-wide bg-slate-900 hover:bg-black text-white active:scale-95' : 'w-full py-4 text-lg font-medium tracking-wide shadow-xl hover:shadow-2xl hover:shadow-slate-900/30 active:scale-[0.98] transition-all duration-200 bg-slate-900 hover:bg-black text-white rounded-2xl'}`}
      >
        {isSearching ? (
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            <span>Searching...</span>
          </div>
        ) : (
          compact ? 'Search' : 'Check Availability'
        )}
      </Button>
    </form>
  );
}
